import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Image, Ruler, Upload, Camera, FileText } from 'lucide-react';

export default function DesignCatalogPage() {
  const [designs, setDesigns] = useState([]);
  const [viewMode, setViewMode] = useState('drawings');
  const [uploading, setUploading] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get('/catalog/designs').then(r => setDesigns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const hasPhoto = (d) => d.image && d.image.startsWith('/products/');
  const hasDrawing = (d) => d.drawing && d.drawing.startsWith('/drawings/');

  const handleImageUpload = async (designId, type, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(designId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/catalog/designs/${designId}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch { alert('Upload failed'); }
    setUploading(null);
    e.target.value = '';
  };

  const getImageSrc = (d) => {
    if (viewMode === 'photos' && hasPhoto(d)) return d.image;
    if (hasDrawing(d)) return d.drawing;
    if (hasPhoto(d)) return d.image;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Design Catalog</h1>
          <p className="text-slate-500 text-sm mt-1">Window & Door types — Technical drawings & product photos</p>
        </div>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="drawings" className="text-xs data-[state=active]:bg-white" data-testid="view-drawings"><Ruler size={14} className="mr-1" /> Drawings</TabsTrigger>
            <TabsTrigger value="photos" className="text-xs data-[state=active]:bg-white" data-testid="view-photos"><Image size={14} className="mr-1" /> Photos</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {designs.map(d => {
          const imgSrc = getImageSrc(d);
          const isPhoto = viewMode === 'photos' && hasPhoto(d);

          return (
            <Card key={d.id} className="border border-slate-200 overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all duration-200" data-testid={`catalog-card-${d.id}`}>
              <div className="relative h-56 bg-white overflow-hidden group border-b border-slate-100">
                {imgSrc ? (
                  <img
                    src={imgSrc + '?v=' + Date.now()}
                    alt={d.name}
                    className={`w-full h-full ${isPhoto ? 'object-cover' : 'object-contain p-2'}`}
                    loading="lazy"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-300 text-sm">No image</div>
                )}

                {/* Upload button */}
                <label className="absolute top-2 right-2 cursor-pointer opacity-70 hover:opacity-100 transition-opacity">
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(d.id, viewMode, e)} className="hidden" />
                  <div className={`bg-[#ed8936] hover:bg-[#dd6b20] text-white rounded-lg px-2.5 py-1.5 text-xs font-medium flex items-center gap-1 shadow-md ${uploading === d.id ? 'opacity-50' : ''}`} data-testid={`upload-btn-${d.id}`}>
                    <Camera size={12} /> {uploading === d.id ? '...' : 'Replace'}
                  </div>
                </label>

                {/* Name overlay for photos */}
                {isPhoto && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <h3 className="font-['Outfit'] font-semibold text-white text-lg drop-shadow-sm">{d.name}</h3>
                  </div>
                )}
              </div>

              <CardContent className="p-4 space-y-3">
                {!isPhoto && <h3 className="font-['Outfit'] font-semibold text-slate-900 text-lg">{d.name}</h3>}
                <p className="text-sm text-slate-600 leading-relaxed">{d.description}</p>
                <div className="space-y-2 text-xs border-t border-slate-100 pt-3">
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 w-20 shrink-0 font-medium">Size Range:</span>
                    <span className="text-slate-700">{d.size_range}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 w-20 shrink-0 font-medium">Glass:</span>
                    <div className="flex flex-wrap gap-1">
                      {d.glass_options?.map((g, i) => (<Badge key={i} variant="secondary" className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200">{g}</Badge>))}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 w-20 shrink-0 font-medium">Hardware:</span>
                    <span className="text-slate-700">{d.hardware}</span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/quotations/new?design=${encodeURIComponent(d.name)}`)}
                  className="w-full h-8 text-xs bg-[#0B2545] hover:bg-[#0a1f3a] text-white"
                  data-testid={`use-in-quotation-${d.id}`}
                >
                  <FileText size={12} className="mr-1" /> Use in Quotation
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
